import { useEffect, useMemo } from 'react';
import { InsuranceResponse } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/api-service/v1/insurance_verification_api.pb';
import {
  InsuranceStatus,
  PayerDetail,
} from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { useInsuranceType } from '../providers/insurance-type-provider';
import { prepareInsuranceDetailsForDental } from '../utils/prepare-data-utils/prepare-insurance-data';
import type { UndefinedOrNull, UseGetInsuranceDetailsDentalOrNonDentalResponse } from '../utils/types';
import { useGetVerifiedInsuranceDetailsShallowStore } from './use-get-verified-insurance-details-store';

export const useGetDentalInsuranceDetails = (
  isDental: boolean,
  personId: string,
  locationId: string,
  insurancePersonDetails: UndefinedOrNull<InsuranceResponse>,
  payerListResponse: UndefinedOrNull<PayerDetail[]>
): UseGetInsuranceDetailsDentalOrNonDentalResponse | null => {
  const { setRefetchInsuranceDetails } = useGetVerifiedInsuranceDetailsShallowStore('setRefetchInsuranceDetails');
  const { insuranceType } = useInsuranceType();
  const {
    data: insuranceVerificationDetails,
    isFetching: isRefetching,
    isLoading,
    refetch,
  } = InsuranceDetailQueries.useGetDentalInsuranceVerificationDetailsPersonId({
    personId,
    locationId: locationId || '',
    insuranceType,
    options: {
      enabled: isDental && !!personId && !!locationId,
    },
  });

  //This preparesInsuranceDetailsForDental including payerListResponse by sanatizing the data and removing unwanted chars
  const insuranceDetails = useMemo(() => {
    return prepareInsuranceDetailsForDental(
      insurancePersonDetails,
      payerListResponse,
      isDental,
      insuranceVerificationDetails?.coverage,
      insuranceType
    );
  }, [insurancePersonDetails, isDental, payerListResponse, insuranceVerificationDetails?.coverage]);

  const isVerificationStatusCompleted = insuranceDetails?.verificationStatus?.toUpperCase() === 'COMPLETED';

  const hasActiveOrVerifiedInsurance =
    (insuranceDetails?.thirdPartyCoverageStatus?.toUpperCase() === InsuranceStatus.ACTIVE ||
      insuranceDetails?.thirdPartyCoverageStatus?.toUpperCase() === InsuranceStatus.VERIFIED) &&
    isVerificationStatusCompleted;

  const hasInactiveInsuranceDetailsFromPartner =
    insuranceDetails?.thirdPartyCoverageStatus?.toUpperCase() === InsuranceStatus.INACTIVE;

  const thirdPartyCoverage = insuranceVerificationDetails?.coverage?.thirdPartyCoverage;

  useEffect(() => {
    if (refetch) {
      setRefetchInsuranceDetails(refetch);
    }
  }, [refetch, setRefetchInsuranceDetails]);

  return {
    isLoading,
    isRefetching,
    refetchInsuranceDetails: refetch,
    insuranceDetails,
    thirdPartyCoverage,
    hasActiveOrVerifiedInsurance,
    hasInactiveInsuranceDetailsFromPartner,
    insuranceVerificationAPIResData: insuranceVerificationDetails,
  };
};
